<template>
  <div class="chat-controls" v-if="!isMobileHome && layoutStore.useChat">
    <!-- 우측 하단에 고정된 채팅 버튼 - 숨김 처리 -->
    <!--
    <Button
      icon="pi pi-comments"
      class="chat-toggle-btn"
      @click="toggleChat"
      :severity="chatStore.hasNewMessages ? 'success' : 'primary'"
    />
    -->

    <div v-show="chatStore.isOpen" class="chat-dialog-wrapper" :style="wrapperStyle">
      <Dialog
        ref="chatDialog"
        v-model:visible="chatStore.isOpen"
        :style="dialogStyle"
        :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
        maximizable
        :draggable="true"
        :modal="false"
        class="chat-dialog"
        @show="onDialogShow"
        @hide="onDialogHide"
        @dragend="onDragEnd"
      >
        <template #header>
          <div class="chat-header">
            <i class="pi pi-comments mr-2"></i>
            <span>AI 채팅 도우미...</span>
          </div>
        </template>

        <!-- 채팅 내용 
        <div class="chat-content" :style="{ height: contentHeight + 'px' }">-->
        <div
          class="chat-content"
          :style="{ height: contentHeight + 'px' }"
        >
          <div
            class="chat-messages"
            ref="messageContainer"
          >
            <div
              v-for="(msg, index) in chatStore.messages"
              :key="index"
              class="message"
              :class="msg.type"
            >
              <div
                v-if="msg.type === 'assistant'"
                v-html="renderMarkdown(msg.content)"
                class="markdown-content"
              ></div>
              <div v-else>{{ msg.content }}</div>
            </div>
          </div>
        </div>

        <!-- 채팅 입력 -->
        <template #footer>
          <div class="chat-input-container">
            <Textarea
              v-model="newMessage"
              rows="1"
              autoResize
              placeholder="메시지를 입력하세요..."
              class="w-full"
              @keydown.enter.exact.prevent="sendMessage"
              @keydown.shift.enter.prevent="newMessage += '\n'"
            />
            <Button
              icon="pi pi-send"
              @click="sendMessage"
              :disabled="!newMessage.trim()"
            />
            <!-- 위치 디버그 확인용 버튼 -->
            <!--
            <Button label="Debug Position" @click="printDialogPosition" />
            <Button label="Move to (100, 200)" @click="moveDialog" />
            -->
          </div>
        </template>

        <!-- 리사이즈 핸들 -->
        <div
          class="resize-handle"
          @mousedown="startResizing"
        ></div>
      </Dialog>
    </div>
  </div>
</template>

<script setup>
  import {
    ref,
    computed,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
  } from 'vue';
  import { useRoute } from 'vue-router';
  import { useChatStore } from '@/stores/chat';
  import { useLayoutStore } from '@/stores/layout';

  import { marked } from 'marked';
  import hljs from 'highlight.js/lib/core';
  import sql from 'highlight.js/lib/languages/sql';
  import javascript from 'highlight.js/lib/languages/javascript';
  import python from 'highlight.js/lib/languages/python';
  import java from 'highlight.js/lib/languages/java';
  import text from 'highlight.js/lib/languages/plaintext';
  import 'highlight.js/styles/github-dark.css';

  // Highlight.js 언어 등록
  hljs.registerLanguage('sql', sql);
  hljs.registerLanguage('javascript', javascript);
  hljs.registerLanguage('python', python);
  hljs.registerLanguage('java', java);
  hljs.registerLanguage('text', text);
  // marked 설정
  marked.setOptions({
    highlight: function (code, lang) {
      // code가 문자열이 아닌 경우 변환
      if (typeof code !== 'string') {
        console.log('Code type in highlight:', typeof code, code);
        code = String(code);
      }

      if (lang && hljs.getLanguage(lang)) {
        try {
          return hljs.highlight(code, { language: lang }).value;
        } catch (err) {
          console.error('Highlight.js error in marked options:', err);
        }
      }
      return code; // 기본값으로 코드 그대로 반환
    },
    breaks: true,
    gfm: true,
    pedantic: false,
    smartLists: true,
    smartypants: true,
  });
  const renderer = new marked.Renderer();
  renderer.code = function (code, language) {
    // 디버깅 로그 추가
    //console.log('Code object:', code);
    //console.log('Language parameter:', language);

    // code가 객체인 경우 처리
    let codeText = code;
    let langType = language;

    if (typeof code === 'object') {
      codeText = code.text || code.raw || JSON.stringify(code);
      langType = code.lang || language; // code 객체의 lang 속성을 우선 사용
    }
    // language가 없거나 지원되지 않는 경우 'text'로 대체
    const validLang =
      langType && hljs.getLanguage(langType) ? langType : 'text';

    try {
      const highlighted = hljs.highlight(codeText, {
        language: validLang,
        ignoreIllegals: true,
      }).value;

      return `<div class="code-block"><button class="copy-code-btn" onclick="copyToClipboard(this)">Copy</button><pre class="language-${validLang}"><code class="hljs language-${validLang}">${highlighted}</code></pre></div>`.trim();
    } catch (err) {
      console.error('Highlight error:', err);
      return `<div class="code-block"><button class="copy-code-btn" onclick="copyToClipboard(this)">Copy</button><pre class="language-text"><code class="hljs language-text">${codeText}</code></pre></div>`.trim();
    }
  };
  // marked 설정 업데이트
  // marked.setOptions({
  //   highlight: function (code, lang) {
  //     // code가 객체인 경우 text 속성 사용
  //     if (typeof code === 'object' && code.text) {
  //       code = code.text;
  //     } else if (typeof code !== 'string') {
  //       code = String(code);
  //     }

  //     if (lang && hljs.getLanguage(lang)) {
  //       try {
  //         return hljs.highlight(code, { language: lang }).value;
  //       } catch (err) {
  //         console.error('Highlight.js error in marked options:', err);
  //       }
  //     }
  //     return code;
  //   },
  //   breaks: true,
  //   gfm: true,
  //   pedantic: false,
  //   smartLists: true,
  //   smartypants: true,
  // });
  marked.use({ renderer });

  // 전역 함수로 정의
  window.copyToClipboard = function (button) {
    const codeBlock = button.nextElementSibling.querySelector('code');
    const text = codeBlock.textContent;

    navigator.clipboard
      .writeText(text)
      .then(() => {
        const originalText = button.textContent;
        button.textContent = 'Copied!';
        button.classList.add('copied');

        setTimeout(() => {
          button.textContent = originalText;
          button.classList.remove('copied');
        }, 2000);
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
        button.textContent = 'Failed to copy';
        button.style.backgroundColor = '#f56565';

        setTimeout(() => {
          button.textContent = 'Copy';
          button.style.backgroundColor = '#2d3748';
        }, 2000);
      });
  };

  // 마크다운 렌더링 함수
  const renderMarkdown = (content) => {
    if (!content) return '';

    // 디버깅을 위한 로그 추가
    //console.log('Content type:', typeof content);
    //console.log('Raw content:', content);

    // 객체인 경우 JSON.stringify 사용
    // if (typeof content === 'object') {
    //   content = JSON.stringify(content);
    // }
    // GPT 응답의 줄바꿈을 마크다운 문법에 맞게 조정
    const processedContent = content
      // 숫자 리스트 앞뒤에 줄바꿈 추가
      .replace(/(\n\d+\.)\s/g, '\n\n$1 ')
      // 들여쓰기 조정: '-'는 항상 숫자 리스트 아래 들여쓰기
      .replace(/\n\s*-\s/g, '\n   - ')
      // 중첩된 리스트가 잘못된 경우 수정
      .replace(/\n(\d+\.)\s*(-)\s/g, '\n$1\n   $2 ')
      // 마지막으로 공백 제거
      .trim();

    try {
      const result = marked.parse(processedContent);
      //console.log('Parsed result:', result); // 파싱 결과 확인
      return result;
    } catch (error) {
      console.error('Markdown parsing error:', error);
      return String(content);
    }
  };

  const route = useRoute();
  const chatStore = useChatStore();
  const layoutStore = useLayoutStore();
  const newMessage = ref('');
  const messageContainer = ref(null);
  const currentPageContent = ref('');

  const chatDialog = ref(null);
  const isInitialized = ref(false);

  // 다이얼로그 크기 관리
  const dialogWidth = ref(
    parseInt(localStorage.getItem('chatDialogWidth')) ||
      Math.round(window.innerWidth * 0.3),
  );
  const dialogHeight = ref(
    parseInt(localStorage.getItem('chatDialogHeight')) ||
      Math.round(window.innerHeight * 0.7),
  );

  // 컨텐츠 영역 높이 계산
  const contentHeight = computed(() => dialogHeight.value - 190);

  // dialogPosition ref 선언
  const dialogPosition = ref(
    JSON.parse(localStorage.getItem('chatDialogPosition')) || null,
  );

  // wrapperStyle computed 속성 추가
  const wrapperStyle = computed(() => ({
    position: 'fixed',
    zIndex: '1000',
    ...(dialogPosition.value && dialogPosition.value.left !== undefined
      ? {
          left: `${dialogPosition.value.left}px`,
          top: `${dialogPosition.value.top}px`,
        }
      : {
          right: '20px',
          bottom: '80px',
        }),
  }));

  // dialogStyle computed 속성 수정
  const dialogStyle = computed(() => ({
    width: dialogWidth.value ? `${dialogWidth.value}px` : '30vw',
    height: dialogHeight.value ? `${dialogHeight.value}px` : '70vh',
    minWidth: '300px',
    minHeight: '400px',
    position: 'relative',
    margin: '0',
  }));

  // onMounted 수정
  onMounted(() => {
    try {
      const savedPosition = localStorage.getItem('chatDialogPosition');
      if (savedPosition) {
        dialogPosition.value = JSON.parse(savedPosition);
      }
    } catch (error) {
      console.error('저장된 위치 파싱 오류:', error);
      dialogPosition.value = null;
    }

    window.addEventListener('resize', adjustDialogPosition);
    isInitialized.value = true;
  });

  // onBeforeUnmount 수정
  onBeforeUnmount(() => {
    window.removeEventListener('resize', adjustDialogPosition);
    isInitialized.value = false;
  });

  // onDialogShow 수정
  const onDialogShow = () => {
    if (!isInitialized.value) return;
    
    // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 실행
    setTimeout(() => {
      adjustDialogPosition();
    }, 100);
  };

  // onDragEnd 함수 추가
  const onDragEnd = () => {
    // 드래그가 끝난 후 위치 조정
    setTimeout(() => {
      adjustDialogPosition();
    }, 50);
  };

  // onDialogHide 함수 추가
  const onDialogHide = () => {
    // 채팅창이 닫힐 때 layout store의 useChat도 false로 설정
    layoutStore.useChat = false;
    localStorage.setItem('useChat', 'false');
  };

  // adjustDialogPosition 수정
  const adjustDialogPosition = () => {
    try {
      if (!isInitialized.value) return;

      // DOM에서 직접 dialog 요소 찾기
      const dialog = document.querySelector('.p-dialog.chat-dialog');
      if (!dialog) {
        console.warn('Dialog element not found for position adjustment');
        return;
      }

      // getBoundingClientRect가 함수인지 확인
      if (typeof dialog.getBoundingClientRect !== 'function') {
        console.warn('getBoundingClientRect is not available on dialog element');
        return;
      }

      const rect = dialog.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;

      let newLeft = rect.left;
      let newTop = rect.top;

      if (rect.right > screenWidth) {
        newLeft = screenWidth - rect.width;
      }
      if (rect.bottom > screenHeight) {
        newTop = screenHeight - rect.height;
      }
      if (rect.left < 0) {
        newLeft = 0;
      }
      if (rect.top < 0) {
        newTop = 0;
      }

      dialogPosition.value = { left: newLeft, top: newTop };
      localStorage.setItem('chatDialogPosition', JSON.stringify(dialogPosition.value));
    } catch (error) {
      console.error('Error in adjustDialogPosition:', error);
    }
  };

  // 실제 위치를 콘솔로 찍어보는 디버그 함수
  const printDialogPosition = () => {
    const dialog = document.querySelector('.p-dialog.chat-dialog');
    if (!dialog) {
      console.log('Dialog not found');
      return;
    }

    const rect = dialog.getBoundingClientRect();
    console.log('Dialog position (rect):', rect);
    console.log('Dialog style.left:', dialog.style.left);
    console.log('Dialog style.top:', dialog.style.top);
    console.log('transform:', dialog.style.transform);
  };
  // **버튼 클릭 시, 강제로 (100, 200) 위치로 이동**
  const moveDialog = () => {
    const dialog = document.querySelector('.p-dialog.chat-dialog');
    if (!dialog) {
      console.warn('Dialog element not found');
      return;
    }

    // 모든 PrimeVue 관련 스타일 초기화
    dialog.style.cssText = `
    position: fixed !important;
    transition: none !important;
    transform: none !important;
    margin: 0 !important;
    left: 100px !important;
    top: 200px !important;
    right: auto !important;
    bottom: auto !important;
  `;

    const newPosition = {
      left: 100,
      top: 200,
    };

    dialogPosition.value = newPosition;
    localStorage.setItem('chatDialogPosition', JSON.stringify(newPosition));

    // 강제 reflow
    dialog.offsetHeight;
  };

  // 현재 페이지 내용 가져오기
  const getCurrentPageContent = () => {
    // 기본 페이지 정보 객체
    const pageInfo = {
      content: '',
      path: route.path,
      name: route.name,
      meta: route.meta,
      title: document.title,
      type: 'unknown',
      isLoginPage: false,
    };

    // 1. 메인 컨텐츠 영역 확인
    const mainContent = document.querySelector('#main-content');
    if (mainContent) {
      pageInfo.content = mainContent.innerText;
    }

    // 2. URL 기반 페이지 타입 식별
    if (route.path.includes('login')) {
      pageInfo.type = 'login';
      pageInfo.isLoginPage = true;
    } else if (route.path.includes('dashboard')) {
      pageInfo.type = 'dashboard';
    }

    // 3. DOM 요소 기반 페이지 타입 식별
    const loginForm =
      document.querySelector('form[action*="login"]') ||
      document.querySelector('.login-form') ||
      document.querySelector('#login-form');
    if (loginForm) {
      pageInfo.type = 'login';
      pageInfo.isLoginPage = true;
    }

    // 4. 특정 클래스나 ID를 통한 페이지 식별
    const bodyClasses = document.body.classList;
    if (
      bodyClasses.contains('login-page') ||
      bodyClasses.contains('auth-page')
    ) {
      pageInfo.type = 'login';
      pageInfo.isLoginPage = true;
    }

    // 5. 메타 태그 정보 수집
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      pageInfo.metaDescription = metaDescription.getAttribute('content');
    }

    // 6. 현재 활성화된 nav 항목 확인
    const activeNavItem = document.querySelector(
      '.nav-item.active, .menu-item.active',
    );
    if (activeNavItem) {
      pageInfo.activeNavSection = activeNavItem.textContent.trim();
    }

    console.log('Current page info:', pageInfo);
    return pageInfo;
  };
  const scrollToBottom = () => {
    if (messageContainer.value) {
      const container = messageContainer.value;
      const scrollHeight = container.scrollHeight;
      const height = container.clientHeight;
      const maxScrollTop = scrollHeight - height;
      container.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
    }
  };
  watch(
    () => chatStore.messages,
    () => {
      nextTick(() => {
        scrollToBottom();
      });
    },
    { deep: true },
  );
  watch(
    () => chatStore.isOpen,
    (isOpen) => {
      if (isOpen) {
        nextTick(() => {
          scrollToBottom();
        });
      }
    },
  );
  // 라우트 변경 감지 및 페이지 내용 업데이트
  watch(
    () => route.path,
    async () => {
      // 라우트 변경 후 DOM 업데이트를 기다림
      await nextTick();
      currentPageContent.value = getCurrentPageContent();
      console.log('Current page content:', currentPageContent.value.name);
    },
    { immediate: true },
  );

  watch(
    () => chatStore.isOpen,
    async (isOpen) => {
      if (isOpen) {
        await nextTick();
        const dialog = document.querySelector('.p-dialog.chat-dialog');
        if (dialog && dialogPosition.value) {
          console.log('Chat dialog opened');
          dialog.style.left = `${dialogPosition.value.left}px`;
          dialog.style.top = `${dialogPosition.value.top}px`;
          dialog.style.removeProperty('right');
          dialog.style.removeProperty('bottom');
        }
      }
    },
  );

  const isResizing = ref(false);
  let startX = 0;
  let startY = 0;

  // 리사이즈 시작 핸들러
  const startResizing = (event) => {
    isResizing.value = true;
    startX = event.clientX;
    startY = event.clientY;

    document.addEventListener('mousemove', resizeDialog);
    document.addEventListener('mouseup', stopResizing);
  };

  // 리사이즈 중
  const resizeDialog = (event) => {
    if (!isResizing.value) return;

    const deltaX = event.clientX - startX;
    const deltaY = event.clientY - startY;

    dialogWidth.value = Math.max(300, dialogWidth.value + deltaX); // 최소 너비 300px
    dialogHeight.value = Math.max(400, dialogHeight.value + deltaY); // 최소 높이 400px

    startX = event.clientX;
    startY = event.clientY;
  };

  // 리사이즈 종료
  const stopResizing = () => {
    isResizing.value = false;
    localStorage.setItem('chatDialogWidth', dialogWidth.value);
    localStorage.setItem('chatDialogHeight', dialogHeight.value);

    document.removeEventListener('mousemove', resizeDialog);
    document.removeEventListener('mouseup', stopResizing);
  };

  const toggleChat = () => {
    chatStore.toggleChat();
    // layout store의 채팅 상태도 토글
    layoutStore.toggleChat();
  };

  // 로그인 폼 제어를 위한 유틸리티 함수들
  const formController = {
    // 이메일 입력
    setEmail: (email) => {
      const emailInput = document.querySelector('#email1');
      if (emailInput) {
        emailInput.value = email;
        // Vue의 v-model을 위한 input 이벤트 발생
        emailInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },

    // 비밀번호 입력
    setPassword: (password) => {
      const passwordInput = document.querySelector('#password1');
      if (passwordInput) {
        passwordInput.value = password;
        passwordInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },

    // 협력사 로그인 체크박스 설정
    setVendorLogin: (isVendor) => {
      const vendorCheckbox = document.querySelector('#vendorLogin');
      if (vendorCheckbox) {
        vendorCheckbox.checked = isVendor;
        vendorCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },

    // 로그인 버튼 클릭
    submitForm: () => {
      const submitButton = document.querySelector('form button[type="submit"]');
      if (submitButton) {
        submitButton.click();
      }
    },
  };

  // sendMessage 함수 내부에서 사용할 로그인 처리 로직
  const handleLoginRequest = async (userMessage) => {
    // 이메일 추출
    const emailMatch = userMessage.match(
      /[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/,
    );
    const email = emailMatch ? emailMatch[0] : '';

    // 비밀번호 추출 (비밀번호, password, pw 등의 키워드 뒤의 문자열)
    const passwordMatch = userMessage.match(
      /(?:비밀번호|password|pw)[:\s]*([^\s]+)/i,
    );
    const password = passwordMatch ? passwordMatch[1] : '';

    // 협력사 로그인 여부 확인
    const isVendorLogin = /협력사|벤더|vendor/i.test(userMessage);

    // 폼 필드 설정
    if (email) formController.setEmail(email);
    if (password) formController.setPassword(password);
    if (isVendorLogin) formController.setVendorLogin(true);

    // 응답 메시지 생성
    let responseMessage = '로그인을 시도합니다.\n';
    if (email) responseMessage += `이메일: ${email}\n`;
    if (isVendorLogin) responseMessage += '계정 유형: 협력사\n';

    // 필수 필드가 모두 있으면 자동 로그인 시도
    if (email && password) {
      setTimeout(() => formController.submitForm(), 500);
      responseMessage += '자동으로 로그인을 진행합니다.';
    } else {
      responseMessage += '이메일 또는 비밀번호가 부족합니다.';
    }

    return responseMessage;
  };

  // LLM에 메시지 전송
  const sendMessage = async () => {
    if (!newMessage.value.trim()) return;

    const userMessage = newMessage.value;
    const pageInfo = getCurrentPageContent();
    // 사용자 메시지 저장
    chatStore.addMessage({
      content: userMessage,
      type: 'user',
      timestamp: new Date(),
    });

    // 현재 페이지 내용 관련 요청인 경우
    if (
      userMessage.includes('현재 페이지') ||
      userMessage.includes('이 페이지')
    ) {
      const prompt = {
        userMessage,
        pageContent: currentPageContent.value,
        currentRoute: route.path,
        pageTitle: document.title,
      };

      try {
        await handleLoginRequest(userMessage);
        // LLM API 호출
        const response =
          "사용인의 실적을 조회하기 위해서는 다음의 절차를 따를 수 있습니다:\n\n1. **사용인 정보 추출**:\n   - 먼저, GA직원기구조직 테이블(M_SAM_GA_STF_SRTR_ORGN)에서 기준년월(ST_YYMM)과 사용인 관련 조건을 사용하여 모든 사용인의 정보를 추출합니다.\n   - 예를 들어, 사용인코드 기준으로 조회할 때는 \"STF_FLGCD IN ('05','06','07','08') AND STF_BZ_STCD='01'\" 조건을 사용하여 추출합니다.\n\n   ```sql\n   DROP TABLE TMP_GA_ORGN;\n   CREATE TABLE TMP_GA_ORGN AS\n   SELECT \n       A.BR_ORGCD AS REGION_CODE, -- 지역단/사업단\n       A.BR_ORGNM AS REGION_NAME,\n       A.BZP_ORGCD AS BRANCH_CODE, -- 지점\n       A.BZP_ORGNM AS BRANCH_NAME,\n       A.PRS_AGYNO AS REP_AGENCY_CODE, -- 대표대리점\n       A.PRS_AGYNM AS REP_AGENCY_NAME,\n       A.ADM_STFNO AS AGENCY_CODE, -- 관리(지사)대리점\n       A.ADM_STFNM AS AGENCY_NAME,\n       A.STFNO AS USRR_CODE, -- 직원코드\n       A.STFNM AS USRR_NAME\n   FROM M_SAM_GA_STF_SRTR_ORGN A\n   WHERE ST_YYMM='202411'\n   AND STF_FLGCD IN ('05','06','07','08')\n   AND STF_BZ_STCD='01';\n   ```\n\n2. **실적 데이터 추출**:\n   - 장기보험수입보험료(M_LCR_LTRM_INCM_PRM) 테이블에서 월납환산보험료 등의 실적 데이터를 추출합니다.\n   - 기준년월(ST_YYMM)과 사용인코드(DH_USR_NO)를 기준으로 Grouping하여 Sum을 통해 실적을 계산합니다.\n\n   ```sql\n   DROP TABLE TMP_GA_ACTIVE_USERS;\n   CREATE TABLE TMP_GA_ACTIVE_USERS AS\n   SELECT \n       A.PVL_YYMM,\n       A.DH_USR_NO AS USRR_CODE, -- 사용인 코드\n       SUM(A.MPY_CV_PRM) AS TOTAL_MPY_CV_PRM, -- 월납환산보험료\n       CASE WHEN SUM(A.MPY_CV_PRM) >= 10000 THEN 1 ELSE 0 END AS TOTAL_10000_PRM_YN -- 월납환산보험료 1만 이상 여부\n   FROM M_LCR_LTRM_INCM_PRM A\n   WHERE A.PVL_YYMM = '202411' -- 기준년월\n   GROUP BY A.PVL_YYMM, A.DH_USR_NO;\n   ```\n\n3. **최종 결과 생성**:\n   - 위에서 생성한 두 개의 임시 테이블을 사용하여 필요한 조건을 추가하고 Join하여 최종 실적 데이터를 생성합니다.\n\n이러한 절차를 통해 사용인의 실적을 효과적으로 조회할 수 있습니다. 각 단계에서 필요한 조건을 추가하여 원하는 데이터를 정확하게 추출할 수 있도록 합니다.";
        chatStore.addMessage({
          content: response,
          type: 'assistant',
          timestamp: new Date(),
        });
        //const response = await callLLMAPI(prompt);
      } catch (error) {
        chatStore.addMessage({
          content: '죄송합니다. 응답을 처리하는 중 오류가 발생했습니다.',
          type: 'error',
          timestamp: new Date(),
        });
      }
    }

    newMessage.value = '';
    // 메시지 전송 후 스크롤 조정
    nextTick(() => {
      scrollToBottom();
    });
  };

  // LLM API 호출 함수
  const callLLMAPI = async (prompt) => {
    // API 엔드포인트 설정
    const response = await fetch('/api/llm', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(prompt),
    });

    if (!response.ok) {
      throw new Error('API 호출 실패');
    }

    return response.text();
  };

  const isMobileHome = computed(() => route.name === 'mobile-home');
</script>

<style lang="scss" scoped>
  .chat-controls {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 10000 !important;
  }

  :deep(.p-dialog.chat-dialog) {
    position: fixed !important;
    margin: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    transition: none !important;
    transform: none !important;
    transform-origin: center !important;

    :deep(.p-dialog-header) {
      flex: 0 0 auto;
      padding: 1rem;
    }

    :deep(.p-dialog-content) {
      flex: 1;
      padding: 0 !important;
      overflow: hidden !important;
      display: flex !important;
      flex-direction: column !important;
    }

    &[style*='left'] {
      right: auto !important;
      bottom: auto !important;
    }
  }

  .chat-content {
    flex: 1;
    overflow-y: auto;
    padding: 0.5rem;
    background-color: var(--surface-ground);
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .chat-messages {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    overflow-y: auto !important;
    max-height: 100%;
  }

  :deep(.p-dialog) {
    :deep(.p-dialog-footer) {
      width: 100%; // 전체 너비 사용
      padding: 1rem; // 패딩 추가
    }
  }

  .chat-input-container {
    flex: 1; // 추가
    display: flex;
    gap: 0.5rem;
    padding: 0.75rem;
    background-color: var(--surface-section);
    border-top: 1px solid var(--surface-border);
    align-items: flex-start; // 상단 정렬로 변경

    :deep(.p-textarea) {
      flex: 1; // 추가: 남은 공간 모두 차지
      width: 100%; // 추가: 전체 너비 사용

      :deep(.p-inputtextarea) {
        width: 100%; // 추가: textarea 자체도 전체 너비 사용
        resize: none;
        max-height: 120px;
        overflow-y: auto;
        min-height: 40px; // 추가: 최소 높이 설정
      }
    }

    // 전송 버튼 크기 조정
    :deep(.p-button) {
      height: 40px; // textarea의 기본 높이와 맞춤
      min-width: 40px;
    }
  }

  .message {
    margin: 0.75rem 0;
    padding: 1rem;
    border-radius: 0.5rem;
    max-width: 85%;
    word-break: break-word;
    flex-shrink: 0;
    white-space: pre-wrap; // 추가: 줄바꿈 유지

    &.user {
      background-color: var(--primary-color);
      color: white;
      margin-left: auto;
    }

    &.assistant {
      background-color: var(--surface-card);
      margin-right: auto;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      :deep(.markdown-content) {
        font-size: 0.95rem;
        line-height: 1.6;

        h1,
        h2,
        h3,
        h4 {
          margin-top: 1em;
          margin-bottom: 0.5em;
          font-weight: 600;
        }

        p {
          margin: 0.8em 0;
          white-space: normal;
        }

        pre {
          margin: 1em 0;
          padding: 2.5em 1em 1em !important;
          border-radius: 6px;
          background-color: #1e1e1e !important;
          overflow-x: auto;
          position: relative !important;
          border: 1px solid #333;
          white-space: pre;

          &[class*='language-']::before {
            content: attr(class);
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 2em;
            padding: 0.3em 1em;
            background: #2d2d2d;
            color: #fff;
            font-size: 0.8em;
            font-weight: 500;
            border-bottom: 1px solid #333;
          }

          &.language-sql::before {
            content: 'SQL';
            background: #2d2d2d;
          }

          &.language-javascript::before {
            content: 'JavaScript';
            background: #2d2d2d;
          }

          code {
            display: block;
            color: #fff;
            background: transparent;
            padding: 0;
            margin: 0;
            font-family: ui-monospace, Monaco, Consolas, monospace;
            font-size: 0.9em;
            line-height: 1.5;
            white-space: pre;
          }

          &::before {
            content: attr(data-language);
            position: absolute;
            top: 0;
            right: 0;
            padding: 0.25em 0.5em;
            font-size: 0.75em;
            color: var(--text-color-secondary);
            background-color: var(--surface-hover);
            border-radius: 0 6px 0 6px;
          }
        }

        code {
          font-family: ui-monospace, Monaco, Consolas, monospace;
          font-size: 0.9em;
          padding: 0.2em 0.4em;
          border-radius: 3px;
          background-color: var(--surface-hover);
        }

        ul,
        ol {
          padding-left: 1.5em;
          margin: 0.8em 0;

          ul,
          ol {
            margin: 0.4em 0;
          }

          li {
            margin: 0.4em 0;

            // 중첩된 목록 항목
            ul,
            ol {
              padding-left: 1.5em;
            }
          }
        }

        blockquote {
          margin: 1em 0;
          padding-left: 1em;
          border-left: 4px solid var(--surface-border);
          color: var(--text-color-secondary);
        }
      }
    }

    &.error {
      background-color: var(--red-100);
      color: var(--red-900);
      margin-right: auto;
      margin-left: auto;
      text-align: center;
      font-weight: 500;
    }
  }

  .resize-handle {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 16px;
    height: 16px;
    background: var(--surface-border);
    cursor: se-resize;
    border-radius: 0 0 4px 0;
  }

  .code-block {
    position: relative;
    margin: 1rem 0;
    display: block; // 추가
    width: 100%; // 추가

    :deep(.copy-code-btn) {
      position: absolute;
      top: 4px; // 살짝 여백
      right: 4px; // 살짝 여백
      z-index: 20; // z-index 높임
      background-color: #2d3748;
      color: #e2e8f0;
      border: 1px solid #4a5568; // 외각선 추가
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); // 그림자 효과
      display: block; // 추가

      &:hover {
        background-color: #4a5568;
        border-color: #718096;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      &:active {
        background-color: #1a202c;
        transform: translateY(1px);
      }

      &.copied {
        // 복사 완료 상태 스타일
        background-color: #48bb78;
        border-color: #38a169;
      }
    }

    :deep(pre) {
      position: relative; // 추가
      margin: 0;
      padding: 1rem;
      background: #1e1e1e;
      border-radius: 6px;
      overflow-x: auto;
      border: 1px solid #2d3748; // 코드 블록 테두리 추가

      code {
        font-family: 'Monaco', 'Consolas', monospace;
        font-size: 0.9em;
        line-height: 1.5;
        white-space: pre;
        word-wrap: normal;
        tab-size: 2;
      }
    }
  }

  :deep(.markdown-content) {
    line-height: 1.6;

    :deep(pre) {
      background: #1e1e1e;
      color: #ffffff;
      padding: 1rem;
      border-radius: 8px;
      overflow-x: auto;
      white-space: pre; // 여기를 수정
      word-wrap: normal; // 여기를 추가
      overflow-wrap: normal; // 여기를 추가
    }

    :deep(code) {
      white-space: pre; // 여기를 수정
      word-wrap: normal; // 여기를 추가
      overflow-wrap: normal; // 여기를 추가
      display: block;
      font-family: ui-monospace, Monaco, Consolas, monospace;
      tab-size: 2; // 탭 크기 지정
    }

    :deep(pre code) {
      display: block;
      white-space: pre-wrap;
    }

    :deep(p) {
      margin: 1rem 0;
    }

    :deep(blockquote) {
      margin: 1rem 0;
      padding: 0.5rem 1rem;
      background: #f5f5f5;
      border-left: 4px solid #ddd;
    }

    :deep(.code-block) {
      position: relative;
      width: 100%;
    }

    /* 숫자 리스트와 하위 리스트 스타일 복원 */
    :deep(ol),
    :deep(ul) {
      list-style-position: inside; /* 리스트 아이템 안에 숫자 또는 불릿 표시 */
      margin-left: 0; /* 기본 들여쓰기 */
      padding-left: 1rem; /* 추가 들여쓰기 */
    }

    :deep(ol) {
      list-style-type: decimal; /* 숫자 리스트 */
    }

    :deep(ul) {
      list-style-type: disc; /* 불릿 리스트 */
    }

    :deep(ol ul),
    :deep(ul ol),
    :deep(ul ul),
    :deep(ol ol) {
      margin-left: 0; /* 하위 리스트도 들여쓰기 */
      padding-left: 1rem; /* 중첩 들여쓰기 최소화 */
    }

    :deep(ol p),
    :deep(ul p) {
      margin: 0; /* 리스트 내부에서 <p> 태그 간격 제거 */
      display: inline; /* 블록 요소 대신 인라인 요소로 처리 */
    }
  }

  :deep(.hljs) {
    padding: 1em;
    margin: 0.5em 0;
    background: #1e1e1e;
    color: #dcdcdc;
    overflow-x: auto;
    border-radius: 6px;
    tab-size: 4;

    :deep(.hljs-keyword),
    :deep(.hljs-selector-tag),
    :deep(.hljs-tag) {
      color: #569cd6;
    }

    :deep(.hljs-string) {
      color: #ce9178;
    }

    :deep(.hljs-comment) {
      color: #6a9955;
    }
  }
</style>
<style lang="scss" scoped>
  .code-block {
    position: relative;
    margin: 1rem 0;
    display: block;
    width: 100%;

    :deep(.copy-code-btn) {
      position: absolute;
      top: 4px; /* 버튼의 상단 위치 */
      right: 4px; /* 버튼의 우측 위치 */
      z-index: 20; /* 우선순위 보장 */
      background-color: #2d3748;
      color: #e2e8f0;
      border: 1px solid #4a5568;
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 0.75rem;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

      &:hover {
        background-color: #4a5568;
        border-color: #718096;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      &:active {
        background-color: #1a202c;
        transform: translateY(1px);
      }

      &.copied {
        background-color: #48bb78;
        border-color: #38a169;
      }
    }
  }
</style>
<style lang="scss" scoped>
  /* 숫자 리스트와 하위 리스트 스타일 */
  :deep(.markdown-content) ol,
  :deep(.markdown-content) ul {
    list-style-position: inside; /* 리스트 아이템 안에 숫자 또는 불릿 표시 */
    margin-left: 0; /* 기본 들여쓰기 */
    padding-left: 1rem; /* 추가 들여쓰기 */
  }

  :deep(.markdown-content) li {
    margin-bottom: 0.4rem; /* 리스트 항목 간격 조정 */
  }

  :deep(.markdown-content) ol {
    list-style-type: decimal; /* 숫자 리스트 */
  }

  :deep(.markdown-content) ul {
    list-style-type: disc; /* 불릿 리스트 */
  }

  :deep(.markdown-content) ol ul,
  :deep(.markdown-content) ul ol,
  :deep(.markdown-content) ul ul,
  :deep(.markdown-content) ol ol {
    margin-left: 0; /* 하위 리스트도 들여쓰기 */
    padding-left: 1rem; /* 중첩 들여쓰기 최소화 */
  }
  :deep(.markdown-content) ol p,
  :deep(.markdown-content) ul p {
    margin: 0; /* 리스트 내부에서 <p> 태그 간격 제거 */
    display: inline; /* 블록 요소 대신 인라인 요소로 처리 */
  }
</style>
<style lang="scss">
  .code-block {
    position: relative; /* 부모 요소 기준으로 자식 위치를 결정 */
    margin: 0;
    display: block;
    width: 100%;
    background: #1e1e1e; /* 코드 블록 배경색 */
    border-radius: 6px;
    padding: 0.5rem; /* 코드 내용 패딩 */
    border: 1px solid #2d3748; /* 코드 블록 테두리 */
    overflow-x: auto; /* 코드 블록 내부 스크롤 */
  }

  .code-block .copy-code-btn {
    position: absolute; /* 부모 요소 기준으로 절대 위치 지정 */
    top: 8px; /* 코드 블록 상단에서 8px 떨어짐 */
    right: 8px; /* 코드 블록 오른쪽에서 8px 떨어짐 */
    z-index: 20; /* 다른 요소 위에 표시 */
    background-color: #2d3748;
    color: #e2e8f0;
    border: 1px solid #4a5568;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  .code-block .copy-code-btn:hover {
    background-color: #4a5568;
    border-color: #718096;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .code-block .copy-code-btn:active {
    background-color: #1a202c;
    transform: translateY(1px);
  }

  .code-block .copy-code-btn.copied {
    background-color: #48bb78;
    border-color: #38a169;
  }
</style>
